import { __awaiter } from "tslib";
import ApiManager from '@/managers/apimanagers';
import { ref } from 'vue';
import MyPusher from './pusher';
import { useSound } from '@/logics/sound';
import dayjs from 'dayjs';
import { useStore } from '@/store/store';
import BigNumber from 'bignumber.js';
let before = dayjs().valueOf();
/**
 * @summary OrderBookのTotal%の色付け
 * @param trade buyまたはsell
 * @param total 合計数量
 */
const setRatioColor = (max, trade, total) => {
    const ratio = (total * 100) / max;
    return `background:
    linear-gradient(
      to left,
      var(--trade-total-${trade}-bgcolor) 0%,
      var(--trade-total-${trade}-bgcolor) ${ratio}%,
      transparent ${ratio}%,
      transparent 100%
    )`;
};
const useOrderBookBuild = (currentList, beforeList, openList) => {
    /**
     * @summary バックエンドとフロントのデータ形式の違いによるOrderBookリストの整形
     * @param trade buyまたはsell
     */
    const build = (trade, isInitBuild = true) => {
        const isSell = trade === 'sell';
        const ASK_OR_BID = isSell ? 'ask' : 'bid';
        if (!currentList[ASK_OR_BID]) {
            return [];
        }
        const max = currentList[ASK_OR_BID].map((c) => Number(c[1])).reduce((before, current) => {
            return before + current;
        }, 0);
        let total = 0;
        let list = currentList[ASK_OR_BID].map((r) => {
            total += Number(r[1]);
            const isMyPosition = openList.some((list) => Number(list.price) === Number(r[0]));
            const current = {
                price: r[0],
                size: r[1],
                trade: trade,
                total: total,
                animation: '',
                css: setRatioColor(max, trade, total),
                isMyPosition: isMyPosition,
            };
            return current;
        }).splice(0, 11);
        list = isSell ? list.reverse() : list;
        return list.map((current, index) => {
            if (isInitBuild) {
                return current;
            }
            return addAnimation(beforeList, current, index);
        });
    };
    /**
     * @summary 前回の板からの差分によりアニメーションをつけるための加工
     * @param before 前回の板情報
     * @param current 今回の板情報
     */
    const addAnimation = (before, current, index) => {
        var _a;
        if ((current.trade === 'buy' && index === 10) || (current.trade === 'sell' && index === 0)) {
            current.animation = '';
            return current;
        }
        const copy = Object.assign({}, current);
        let existsPrice = null;
        for (const l of before) {
            if (l.price === current.price) {
                existsPrice = l;
                break;
            }
        }
        if (!existsPrice) {
            if (before[index]) {
                copy.animation = ((_a = before[index]) === null || _a === void 0 ? void 0 : _a.animation) !== 'positive1' ? 'positive1' : 'positive0';
            }
            else {
                copy.animation = 'positive0';
            }
            return copy;
        }
        const currentSize = Number(copy.size);
        const beforeSize = Number(existsPrice.size);
        if (existsPrice) {
            if (currentSize > beforeSize) {
                copy.animation = existsPrice.animation !== 'positive1' ? 'positive1' : 'positive0';
            }
            else if (currentSize < beforeSize) {
                copy.animation = existsPrice.animation !== 'negative1' ? 'negative1' : 'negative0';
            }
            else {
                copy.animation = before[index] ? before[index].animation : 'positive0';
            }
        }
        return copy;
    };
    return { build, addAnimation };
};
/**
 * @summary 板情報取得
 */
export const useOrderBook = () => {
    const store = useStore();
    /**
     * @param pair 通貨ペア
     * @returns 板情報のリスト
     */
    const getOrderBook = (pair) => __awaiter(void 0, void 0, void 0, function* () {
        const limit = 11;
        const url = `/orderbook?pair=${pair}&limit=${limit}`;
        const list = yield ApiManager.get(url).catch((err) => {
            return Promise.reject(err);
        });
        const { build } = useOrderBookBuild(list, [], store.state.openList);
        return { buy: build('buy'), sell: build('sell') };
    });
    /**
     * @summary 板のスプレッドの計算
     */
    const orderBookSpread = (buyList, sellList, decimal) => {
        var _a, _b;
        const reverseSellList = [...sellList].reverse();
        const bid = ((_a = [...buyList][0]) === null || _a === void 0 ? void 0 : _a.price) || 0;
        const ask = ((_b = reverseSellList[0]) === null || _b === void 0 ? void 0 : _b.price) || 0;
        const isZeroSymbolPrice = bid === 0 || ask === 0;
        return isZeroSymbolPrice ? '0' : BigNumber(bid).minus(BigNumber(ask)).abs().dp(decimal).toNumber().toFixed(decimal);
    };
    return { getOrderBook, orderBookSpread };
};
/**
 * @summary 板情報の購読
 */
export const orderBookSubscriber = (buy, sell, pair) => {
    const store = useStore();
    const buyList = ref(buy);
    const sellList = ref(sell);
    let channel;
    let beforeOrderBook;
    const { orderbook, play } = useSound();
    const subscribe = () => {
        channel = MyPusher.subscribe('order-book');
        channel.bind(`order-book-${pair.toLowerCase()}`, bind);
    };
    const unsubscribe = () => {
        channel === null || channel === void 0 ? void 0 : channel.unbind();
        channel === null || channel === void 0 ? void 0 : channel.unsubscribe();
    };
    const bind = (data) => {
        if (JSON.stringify(data) === JSON.stringify(beforeOrderBook)) {
            return;
        }
        if (JSON.stringify(data === null || data === void 0 ? void 0 : data.ask) !== JSON.stringify(beforeOrderBook === null || beforeOrderBook === void 0 ? void 0 : beforeOrderBook.ask)) {
            sellList.value = useOrderBookBuild(data, [...sellList.value], store.state.openList).build('sell', false);
        }
        if (JSON.stringify(data === null || data === void 0 ? void 0 : data.bid) !== JSON.stringify(beforeOrderBook === null || beforeOrderBook === void 0 ? void 0 : beforeOrderBook.bid)) {
            buyList.value = useOrderBookBuild(data, [...buyList.value], store.state.openList).build('buy', false);
        }
        const now = dayjs().valueOf();
        if (before + 1000 * Math.random() > now) {
            play(orderbook);
        }
        before = now;
        beforeOrderBook = data;
    };
    return { subscribe, unsubscribe, bind, buyList, sellList };
};
